/**
 * U0 · four states, one component: `ready · partial · blocked · not started`.
 *
 * Used by the designer for recipe readiness and by the library for library coverage. They ask
 * different questions and get the same shape of answer, which is the point — an author who
 * learned to read one has learned to read both.
 *
 * **Blocking is separated from completeness**, deliberately. "12 things are unfinished" and
 * "1 of them stops this being issued" are different facts, and merging them into a progress
 * number is how the one that matters gets lost in the eleven that do not.
 *
 * Every row carries what it refers to, so a count is somewhere you can go. A number you can
 * read and cannot act on is not coverage, it is decoration.
 */
import React from "react";
import { Badge, Button, Flex, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

export const STATES = {
  ready: { label: "ready", tone: "success" },
  partial: { label: "partial", tone: "warning" },
  blocked: { label: "blocked", tone: "error" },
  not_started: { label: "not started", tone: "default" },
};

export default function CoveragePanel({
  title = "Coverage",
  headline,
  note,
  blocking = 0,
  advisory = 0,
  rows = [],
  active,
  onPick,
}) {
  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="baseline">
        <span className="u0-eyebrow">{title}</span>
        {/* Two facts, never one number: what stops this, and what is merely unfinished. */}
        <Flex gap={6}>
          {blocking ? <span className="u0-pill block">{blocking} blocking</span> : null}
          {advisory ? <span className="u0-pill warn">{advisory} advisory</span> : null}
          {!blocking && !advisory ? <span className="u0-pill ok">clear</span> : null}
        </Flex>
      </Flex>

      {headline ? (
        <div className="u0-cvhead">
          <Text strong style={{ display: "block", marginBottom: 6 }}>{headline}</Text>
          {note ? <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>{note}</Text> : null}
        </div>
      ) : null}

      <Flex vertical gap={2}>
        {rows.map((r) => (
          <Button
            key={r.key}
            type="text"
            size="small"
            onClick={() => onPick?.(active === r.key ? null : r.key)}
            className={`u0-cvrow ${active === r.key ? "on" : ""}`}
          >
            <Flex align="flex-start" gap={8} style={{ width: "100%" }}>
              <Badge status={r.severity === "blocking" ? "error" : "warning"} />
              <Flex vertical style={{ flex: 1, whiteSpace: "normal", textAlign: "left" }}>
                <Flex gap={6} align="baseline">
                  <Text strong>{r.count}</Text>
                  <Text>{r.label}</Text>
                  {r.why ? (
                    <Tooltip title={r.why} overlayStyle={{ maxWidth: 360 }}>
                      <InfoCircleOutlined style={{ color: "var(--dz-faint)" }} />
                    </Tooltip>
                  ) : null}
                </Flex>
                {r.detail?.length ? (
                  <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>
                    {r.detail.map((d) => (Array.isArray(d) ? d.join(" / ") : d)).join(" · ")}
                  </Text>
                ) : null}
              </Flex>
            </Flex>
          </Button>
        ))}
      </Flex>
    </Flex>
  );
}
